import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Package, User, LogOut, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";

export default function AccountPage() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const isOrdersTab = location.pathname.includes("/orders");

  useEffect(() => {
    if (!authLoading && (!user || user === false)) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/orders")
        .then(({ data }) => setOrders(data))
        .catch(() => {})
        .finally(() => setLoadingOrders(false));
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (authLoading || !user || user === false) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  const statusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-green-100 text-green-800",
      shipped: "bg-blue-100 text-blue-800",
      delivered: "bg-emerald-100 text-emerald-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div data-testid="account-page" className="min-h-screen">
      <SEO title="My Account | AERWEAR" description="AERWEAR account and order tools." noindex />
      {/* Header */}
      <div className="border-b border-[#0A0A0A] py-12 px-6 text-center">
        <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk']">My Account</h1>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            data-testid="tab-profile"
            onClick={() => navigate("/account")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] border-b-2 transition-colors ${
              !isOrdersTab ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            <User size={14} className="inline mr-2" /> Profile
          </button>
          <button
            data-testid="tab-orders"
            onClick={() => navigate("/account/orders")}
            className={`px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] border-b-2 transition-colors ${
              isOrdersTab ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            <Package size={14} className="inline mr-2" /> Orders
          </button>
        </div>

        {/* Profile Tab */}
        {!isOrdersTab && (
          <div data-testid="profile-section">
            <div className="border border-gray-200 p-8">
              <div className="space-y-4">
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] text-gray-400 font-['Outfit']">Name</span>
                  <p className="text-sm font-semibold font-['Outfit'] mt-1">{user.name}</p>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] text-gray-400 font-['Outfit']">Email</span>
                  <p className="text-sm font-semibold font-['Outfit'] mt-1">{user.email}</p>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] text-gray-400 font-['Outfit']">Role</span>
                  <p className="text-sm font-semibold font-['Outfit'] mt-1 uppercase">{user.role}</p>
                </div>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="mt-6 flex items-center gap-2 border border-gray-300 px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] hover:border-[#0A0A0A] transition-colors"
            >
              <LogOut size={14} /> Log Out
            </button>
          </div>
        )}

        {/* Orders Tab */}
        {isOrdersTab && (
          <div data-testid="orders-section">
            {loadingOrders ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 border border-gray-200">
                <Package size={40} className="mx-auto text-gray-300 mb-4" />
                <p className="text-sm text-gray-500 font-['Outfit']">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} data-testid={`order-${order.id}`} className="border border-gray-200 p-6 hover:border-[#0A0A0A] transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-xs text-gray-400 font-['Outfit'] uppercase tracking-wider">Order #{order.id?.slice(0, 8)}</p>
                        <p className="text-xs text-gray-400 font-['Outfit'] mt-1">
                          {new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                        </p>
                      </div>
                      <span className={`text-xs uppercase tracking-wider font-semibold px-3 py-1 ${statusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm font-['Outfit']">
                          <span>{item.name} ({item.size}/{item.color}) x{item.quantity}</span>
                          <span>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 mt-4 pt-3 flex justify-between">
                      <span className="text-xs uppercase tracking-wider font-semibold font-['Outfit']">Total</span>
                      <span className="text-sm font-bold font-['Outfit']">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
